import { useState, useEffect, useCallback } from 'react';
import { auth } from '../../services/firebase';
import { onAuthStateChanged, signInWithCustomToken, onIdTokenChanged } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { 
  saveUserToFirestore, 
  getUserEmailFromFirestore
} from '../../utils';
import { useCustomEmailAuth } from './useCustomEmailAuth';
import { useEmailVerification } from './useEmailVerification';
import { usePasswordReset } from './usePasswordReset';
import { useGoogleAuth } from './useGoogleAuth';
import { useKakaoAuth } from './useKakaoAuth';
import { useNaverAuth } from './useNaverAuth';
import { useLogout } from './useLogout';
import { useGlobalAlert } from '../useModal';

export const useAuth = () => {
  const { i18n } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [user, setUser] = useState(null);
  const [userPhoto, setUserPhoto] = useState("");
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // 🔧 Firestore 저장 상태 추적 (무한루프 방지)
  const [lastSavedUserId, setLastSavedUserId] = useState(null);

  // AlertDialog 시스템
  const { alertDialog, showAlert, closeAlert } = useGlobalAlert();

  // 분리된 훅들 사용
  // 커스텀 이메일 시스템 사용
  const emailAuth = useCustomEmailAuth();
  const emailVerification = useEmailVerification();
  const passwordReset = usePasswordReset();
  const googleAuth = useGoogleAuth(setLoginDialogOpen, emailAuth.setAuthLoading);
  const kakaoAuth = useKakaoAuth(setLoginDialogOpen, showAlert, emailAuth.setAuthLoading);
  const naverAuth = useNaverAuth(setLoginDialogOpen, showAlert, emailAuth.setAuthLoading);
  const logout = useLogout(setIsLoggedIn, setUserEmail, setLogoutDialogOpen, emailAuth.resetEmailForm);

  const handleNativeLogin = useCallback(async (token) => {
    try {
      console.log('✅ [useAuth] Handling native login...');
      const userCredential = await signInWithCustomToken(auth, token);
      const user = userCredential.user;
      
      if (user) {
        setIsLoggedIn(true);
        setUserId(user.uid);
        setUser(user);
        
        let userEmailToSet = user.email || await getUserEmailFromFirestore(user.uid) || "";
        setUserEmail(userEmailToSet);
        setUserPhoto(user.photoURL || "");
        
        await saveUserToFirestore(user, {});
        
        setLoginDialogOpen(false); // 로그인 모달 닫기
        
        // 🔧 iOS 구글 로그인 로딩 스피너 종료
        if (emailAuth.setAuthLoading) {
          emailAuth.setAuthLoading(false);
          console.log('🔄 iOS 네이티브 로그인 로딩 종료');
        }
        
        console.log('✅ [useAuth] Native login successful, state updated.');
        return user; // 성공 시 사용자 정보 반환
      }
    } catch (error) {
      console.error('❌ [useAuth] Native login failed:', error);
      
      // 🔧 에러 시에도 로딩 스피너 종료
      if (emailAuth.setAuthLoading) {
        emailAuth.setAuthLoading(false);
        console.log('🔄 iOS 네이티브 로그인 로딩 종료 (에러)');
      }
      
      showAlert('error', '로그인 실패', '로그인 처리 중 오류가 발생했습니다.');
      return null; // 실패 시 null 반환
    }
  }, [showAlert, emailAuth.setAuthLoading]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔄 [DEBUG] Firebase 인증 상태 변경:', { user: !!user, uid: user?.uid });
      
      if (user) {
        setIsLoggedIn(true);
        setUserId(user.uid);
        setUser(user);
        
        // 이메일 설정 - Firebase에서 없으면 Firestore에서 가져오기
        let userEmailToSet = user.email;
        
        if (!userEmailToSet) {
          userEmailToSet = await getUserEmailFromFirestore(user.uid) || "";
        }
        
        setUserEmail(userEmailToSet || "");
        setUserPhoto(user.photoURL || "");
        
        // 🔧 무한루프 방지: 처음 로그인이거나 다른 사용자인 경우에만 Firestore 저장
        if (lastSavedUserId !== user.uid) {
          console.log('💾 [DEBUG] 새로운 사용자 또는 첫 로그인 - Firestore 저장');
          await saveUserToFirestore(user, {});
          setLastSavedUserId(user.uid);
        } else {
          console.log('🚫 [DEBUG] 이미 저장된 사용자 - Firestore 저장 생략 (무한루프 방지)');
        }
        
        console.log('✅ [DEBUG] 사용자 인증 상태 업데이트 완료');
      } else {
        console.log('🚪 [DEBUG] 로그아웃 또는 초기 상태');
        setIsLoggedIn(false);
        setUserEmail("");
        setUserId("");
        setUser(null);
        setUserPhoto("");
        setLastSavedUserId(null); // 로그아웃 시 초기화
      }
    });
    return () => unsubscribe();
  }, [lastSavedUserId]);

  // ID 토큰 변경 감지 (이메일 인증 상태 변경 등)
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user && isLoggedIn) {
        // 이메일 인증 상태가 변경된 경우 상태 업데이트
        console.log('🔄 [DEBUG] ID 토큰 변경 감지 - emailVerified:', user.emailVerified);
        setUser(user); // 사용자 객체 상태 업데이트
      }
    });
    
    return () => unsubscribe();
  }, [isLoggedIn]);

  return {
    // 기본 인증 상태
    isLoggedIn,
    userEmail,
    userId,
    user,
    userPhoto,
    loginDialogOpen,
    setLoginDialogOpen,
    logoutDialogOpen,
    setLogoutDialogOpen,
    
    // 이메일 인증 관련
    isSignup: emailAuth.isSignup,
    setIsSignup: emailAuth.setIsSignup,
    email: emailAuth.email,
    setEmail: emailAuth.setEmail,
    password: emailAuth.password,
    setPassword: emailAuth.setPassword,
    handleEmailAuth: emailAuth.handleEmailAuth,
    authLoading: emailAuth.authLoading,
    setAuthLoading: emailAuth.setAuthLoading, // 🔧 로딩 스피너 제어를 위해 추가
    authError: emailAuth.authError,
    setAuthError: emailAuth.setAuthError,
    
    // 이메일 인증 재발송 관련
    isResending: emailVerification.isResending,
    resendEmailVerification: emailVerification.resendEmailVerification,
    
    // 비밀번호 재설정 관련
    resetPasswordMode: passwordReset.resetPasswordMode,
    setResetPasswordMode: passwordReset.setResetPasswordMode,
    resetPasswordEmail: passwordReset.resetPasswordEmail,
    setResetPasswordEmail: passwordReset.setResetPasswordEmail,
    resetPasswordSuccess: passwordReset.resetPasswordSuccess,
    resetPasswordError: passwordReset.resetPasswordError,
    handlePasswordReset: passwordReset.handlePasswordReset,
    
    // 소셜 인증 관련
    handleGoogleAuth: googleAuth.handleGoogleAuth,
    handleKakaoAuth: kakaoAuth.handleKakaoAuth,
    handleNaverAuth: naverAuth.handleNaverAuth,
    handleLogout: logout.handleLogout,
    handleNativeLogin,
    
    // AlertDialog 시스템
    alertDialog,
    showAlert,
    closeAlert
  };
};